import type { IPortfolioEpicCompletionResponse } from '@skystone/shared';
import {
  EPIC_DONE_BEFORE_COLOR,
  EPIC_DONE_IN_PERIOD_COLOR,
  EPIC_REMAINING_COLOR,
} from '@/components/shared/epic-completion-chart';

interface IEpicCompletionListProps {
  data: IPortfolioEpicCompletionResponse;
  /** When true, skip the inline progress bar (narrow-column fallback) */
  minimal?: boolean;
}

/**
 * Narrow-width fallback for the Epic Completion bar chart.
 *
 * Renders each epic as a row with name and three-bucket counts (Done before /
 * Done this period / Remaining), and unless `minimal` is set, a thin inline
 * three-segment progress bar matching the full chart's colours. Below ~280px
 * card width, the parent wrapper sets minimal=true and the bar disappears —
 * headline + numbers only.
 */
export function EpicCompletionList({ data, minimal = false }: Readonly<IEpicCompletionListProps>) {
  if (data.epics.length === 0) return null;

  return (
    <ul className="space-y-2">
      {data.epics.map((e) => {
        // Derive everything from the buckets so the row's pct agrees with
        // the count text and the inline progress bar. The SQL invariant
        // `done = doneBefore + doneInPeriod` makes this identical to e.done
        // for current windows; for historical windows or legacy responses,
        // this stays consistent with what the segments paint.
        const doneInPeriod = e.doneInPeriod ?? 0;
        const doneBefore = e.doneBefore ?? Math.max(0, e.done - doneInPeriod);
        const doneAtPeriodEnd = doneBefore + doneInPeriod;
        const pct = e.total > 0 ? Math.round((doneAtPeriodEnd / e.total) * 100) : 0;
        const beforePct = e.total > 0 ? (doneBefore / e.total) * 100 : 0;
        const inPeriodPct = e.total > 0 ? (doneInPeriod / e.total) * 100 : 0;
        const remainingPct = Math.max(0, 100 - beforePct - inPeriodPct);
        return (
          <li key={e.epicKey} className="space-y-1">
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="truncate font-medium text-foreground" title={`${e.epicName} (${e.productName})`}>
                {e.epicName}
                <span className="ml-1 text-muted-foreground">({e.productName})</span>
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {doneBefore}+<span className="text-primary">{doneInPeriod}</span>/{e.total} · {pct}%
              </span>
            </div>
            {!minimal && (
              <div
                className="flex h-1 w-full overflow-hidden rounded-sm bg-border"
                aria-hidden="true"
              >
                <div
                  className="h-full"
                  style={{ width: `${beforePct}%`, backgroundColor: EPIC_DONE_BEFORE_COLOR }}
                />
                <div
                  className="h-full"
                  style={{ width: `${inPeriodPct}%`, backgroundColor: EPIC_DONE_IN_PERIOD_COLOR }}
                />
                <div
                  className="h-full"
                  style={{
                    width: `${remainingPct}%`,
                    backgroundColor: EPIC_REMAINING_COLOR,
                    opacity: 0.5,
                  }}
                />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
